import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import sortBy from 'lodash/sortBy';
import classNames from 'classnames';

import { Mobile, Desktop } from 'app/components/Responsive';

import { LocaleContext } from 'app/containers/LocaleProvider';
import TextLine from 'app/components/ui/TextLine';

import { PromoCheckoutPatchedDishesContext } from '../PromoCheckoutMenuDishesAdapter';
import { ConfigContext } from '../ConfigProvider';
import { BasketContext } from '../BasketProvider';

import BasketDish from './BasketDish';
import CheckoutTitle from '../Checkout/CheckoutTitle';

import './basket-dishes.scss';

export default function BasketDishes({
    hasTopIndent = false,
    dishes,
    buttonsDisabled,
    showDeliveryPrice = true,
}) {
    const {
        dishesArray,
    } = useContext(PromoCheckoutPatchedDishesContext);

    const config = useContext(ConfigContext);
    const { applyPromocodePercentToBasketDishes } = config;

    const {
        promocodeConditions: {
            discountType,
            discountValue,
            isPromoCodeActivated,
        },
        basket: { totals },
    } = useContext(BasketContext);

    const isPercentDiscount = discountType === 1 && discountValue > 0;

    const isNeededApplyDiscountToDishesPrice = (
        isPromoCodeActivated
        && isPercentDiscount
        && applyPromocodePercentToBasketDishes
    );

    const { locale } = useContext(LocaleContext);

    const dishesWithData = dishes
        .filter((d) => d?.id !== '4847' && d?.id !== '5267')
        .map((d) => {
            const defaultDishData = {
                id: d.id,
                category: '',
                originalPriceWithStep: d.itemPrice.toLocaleString('RU'),
                discountPriceWithStep: d.itemPrice.toLocaleString('RU'),
                imageUrl: d.previewMobileImage,
                imageUrlDesktop: d.previewImage,
                dishCountStep: d.portions[0],
            };

            const title = locale === 'en' ? (d.titleEn || d.title) : d.title;
            const caption = locale === 'en' ? (d.captionEn || d.caption) : d.caption;

            const dishData = dishesArray.find((arrayDishData) => {
                const isDishInArray = d.id === arrayDishData.id;
                const isDishInSet = arrayDishData.isSet && arrayDishData.dishes.includes(d.id);
                return isDishInArray || isDishInSet;
            }) || defaultDishData;

            const {
                id,
                isSet,
                category,
                originalPriceWithStep,
                discountPriceWithStep,
                imageUrl,
                imageUrlDesktop,
                dishCountStep,
                giftWrapId,
            } = dishData;

            const priceNum = Number(discountPriceWithStep);

            const discountPrice = isNeededApplyDiscountToDishesPrice && !Number.isNaN(priceNum)
                ? String(Math.round((priceNum * (100 - discountValue)) / 100))
                : discountPriceWithStep;

            return {
                ...d,
                title,
                caption,
                isSet,
                id,
                key: d.id,
                portionPrice: d.price,
                category,
                originalPriceWithStep: isSet ? defaultDishData.originalPriceWithStep : originalPriceWithStep,
                discountPriceWithStep: isSet ? defaultDishData.discountPriceWithStep : discountPrice,
                imageUrl,
                imageUrlDesktop,
                dishCountStep,
                giftWrapId,
                isGift: d.sectionTitle === 'Подарок',
            };
        });

    const sortedDishes = sortBy(dishesWithData, [
        (d) => {
            if (d.isSet) {
                const setNum = d.id.split('_')[1];
                return Number(setNum);
            }
            return true;
        },
        (d) => d.portionPrice,
    ]);

    const subtitles = {
        IN: '(бесплатно при заказе от 2400 ₽)',
        OUT: '(200 ₽ при заказе от 2400 ₽)',
    };
    const hit = (totals.delivery_price === 600 || totals.delivery_price === 200) ? 'OUT' : 'IN';
    const deliverySubtitle = subtitles[hit];

    const listClasses = classNames({
        'ny__basket-dishes__dishes-list': true,
        'has-top-indent': hasTopIndent,
    });

    return (
        <div styleName="ny__basket-dishes__root">
            <Mobile isWideMobile>
                <div styleName="ny__basket-dishes__title-container">
                    <CheckoutTitle>
                        В Вашем заказе:
                    </CheckoutTitle>
                </div>
            </Mobile>
            <div styleName={listClasses}>
                {sortedDishes.map((dish) => (
                    <BasketDish
                        key={dish.key || dish.id}
                        buttonsDisabled={buttonsDisabled}
                        {...dish}
                    />
                ))}
                {showDeliveryPrice && (
                    <div styleName="ny__basket-dish__root">
                        <Desktop>
                            <div styleName="ny__basket-dish__image" />
                        </Desktop>

                        <div styleName="ny__basket-dish__right-side">
                            <div styleName="ny__basket-dish__title-wrap ny__basket-dish__title-wrap--wide">
                                <p styleName="ny__basket-dish__title ny__basket-dish__title--one-line">
                                    Доставка
                                </p>

                                <div styleName="ny__basket-dish__price-container">
                                    {totals.delivery_price === 0 ? (
                                        <span styleName="ny__basket-dish__discount-price">
                                            Бесплатно
                                        </span>
                                    ) : (
                                        <span styleName="ny__basket-dish__discount-price">
                                            <span styleName="num">
                                                {totals.delivery_price}
                                                &nbsp;
                                            </span>
                                            ₽
                                            {totals.delivery_price !== 200 && (
                                                <>
                                                    &nbsp;
                                                    <Mobile><br /></Mobile>
                                                    <TextLine>{deliverySubtitle}</TextLine>
                                                </>
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

BasketDishes.propTypes = {
    dishes: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string.isRequired,
        portion: PropTypes.number.isRequired,
    })).isRequired,
    buttonsDisabled: PropTypes.bool.isRequired,
    hasTopIndent: PropTypes.bool,
    showDeliveryPrice: PropTypes.bool,
};
